import React, { useEffect, useState } from "react";

import { useError } from "../../../contexts/ErrorContext";
import useApi from "../../../hooks/useGaneshApi";

import { GridColDef, GridInitialState } from "@mui/x-data-grid-premium";
import { OmniSummary_T } from "../../../types/Brand";
import { getDateValue, renderDateCell, renderNumberCell } from "utils/dataGrid";
import GaneshDataGrid from "../../DataGrid/GaneshDataGrid";

interface OmniCountriesSummaryTableProps {
  omni_key?: string;
}

const defaultGridState: GridInitialState = {
  filter: {
    filterModel: {
      items: [],
    },
  },
  sorting: { sortModel: [{ field: "country", sort: "asc" }] },
  pagination: { paginationModel: { pageSize: 25, page: 0 } },
  pinnedColumns: { left: [] },
  columns: { columnVisibilityModel: {} },
  density: "compact",
  aggregation: {},
};
const columns_def: GridColDef[] = [
  {
    field: "country",
    headerName: "Country",
    description:
      "Country Name in this attribute come from a list of distinct country names where places associated with this brand are located, derived from the Omniscraper data.",
    width: 200,
  },
  {
    field: "country_code",
    headerName: "Country Code",
    description:
      "Country Code in this attribute come from a list of distinct country codes where places associated with this brand are located, derived from the Omniscraper data.",
    width: 200,
  },
  {
    field: "omni_place_count",
    headerName: "Total Omni Places",
    description:
      "The total number of places associated with this brand that have an external_id for an Omni scraper.",
    type: "number",
    width: 100,
    renderCell: renderNumberCell,
  },
  {
    field: "omni_active_count",
    headerName: "Active Omni Places",
    description:
      "The number of non-ignored, non-duplicate places associated with this brand that have an external_id for an Omni scraper.",
    type: "number",
    width: 100,
    renderCell: renderNumberCell,
  },
  {
    field: "open_omni_place_count",
    headerName: "Open Omni Places",
    description: "The number of active Omni places where the status is 'open.'",
    type: "number",
    width: 100,
    renderCell: renderNumberCell,
  },
  {
    field: "closed_omni_place_count",
    headerName: "Closed Omni Places",
    description:
      "The number of active Omni places where the status is 'closed.'",
    type: "number",
    width: 100,
    renderCell: renderNumberCell,
  },
  {
    field: "omni_duplicate_count",
    headerName: "Duplicate Omni Places",
    description:
      "The number of Omni places where parent_id is not null, indicating duplicates.",
    type: "number",
    width: 100,
    renderCell: renderNumberCell,
  },
  {
    field: "building_polygon_count",
    headerName: "Building Polygon Count",
    description:
      "The number of active Omni places with a non-null polygon value.",
    type: "number",
    width: 100,
    renderCell: renderNumberCell,
  },
  {
    field: "omni_first_observation",
    headerName: "First Observation",
    description:
      "The date when the first Omni observation was recorded for this brand.",
    type: "date",
    width: 150,
    valueGetter: getDateValue,
    renderCell: renderDateCell,
  },
  {
    field: "omni_last_observation",
    headerName: "Last Observation",
    description:
      "The date of the most recent Omni observation recorded for this brand.",
    type: "date",
    width: 150,
    valueGetter: getDateValue,
    renderCell: renderDateCell,
  },
  {
    field: "frozen_at",
    headerName: "Frozen At",
    description:
      "The date the brand was marked as frozen, indicating that it has been approved and no further changes can be made except for updates related to Omniscraper observations and manual observations.",
    type: "date",
    width: 150,
    valueGetter: getDateValue,
    renderCell: renderDateCell,
  },
  {
    field: "query_last_executed",
    headerName: "Data Last Updated",
    description:
      "The date when the data for this omni was last retrieved and aggregated from the production database.",
    type: "date",
    width: 150,
    valueGetter: getDateValue,
    renderCell: renderDateCell,
  },
];

const OmniCountriesSummaryTable: React.FC<OmniCountriesSummaryTableProps> = ({
  omni_key,
}) => {
  const [omniCountriesSummaryData, setOmniCountriesSummaryData] = useState<
    OmniSummary_T[]
  >([]);
  const [loading, setLoading] = useState(true);

  const { showError } = useError();
  const { apiRequest } = useApi();

  useEffect(() => {
    if (omni_key) {
      const fetchOmniCountriesSummaryData = async () => {
        try {
          const response = await apiRequest<OmniSummary_T[]>(
            `/brand_reports/omni_countries_summary/${omni_key}`,
            {
              method: "GET",
            }
          );
          if (response.data) {
            setOmniCountriesSummaryData(response.data);
          }
        } catch (error) {
          console.error(error);
          showError(`Failed to fetch omni countries summary for ${omni_key}.`);
        } finally {
          setLoading(false);
        }
      };
      fetchOmniCountriesSummaryData();
    }
  }, [showError]);

  return (
    <GaneshDataGrid
      loading={loading}
      data={omniCountriesSummaryData}
      columns={columns_def}
      gridId="omniCountriesSummaryGrid"
      defaultGridState={defaultGridState}
    />
  );
};
export default OmniCountriesSummaryTable;
